import React, { useState, useMemo, useEffect } from 'react';
import { Info, ArrowLeftRight } from 'lucide-react';
import HoverImage from './HoverImage';

const ItemList = ({ items, onEdit, onDelete, settings, themeClasses, isDarkMode, groupBy: groupByProp, onGroupByChange }) => {
  const [groupBy, setGroupBy] = useState(() => {
    if (groupByProp) return groupByProp;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('groupBy') || 'none';
    } catch {
      return 'none';
    }
  });
  const [expandedVariants, setExpandedVariants] = useState({});
  const [flippedCards, setFlippedCards] = useState({}); // Track which cards show front vs back

  // Sync local state with prop when parent controls groupBy
  useEffect(() => {
    if (groupByProp !== undefined && groupByProp !== groupBy) {
      setGroupBy(groupByProp);
    }
  }, [groupByProp]);

  // 1. Process items to consolidate "Variants" (Same Country + Year + Denomination)
  // This runs regardless of the visual grouping.
  const displayItems = useMemo(() => {
      if (!items) return [];

      const variantMap = new Map();
      
      const includeYear = settings?.variantIncludeYear ?? true;

      items.forEach(item => {
          // Create a unique key for the "Coin Identity"
          // Normalize to lower case and trim to ensure matching
          const c = (item.country || 'unknown').toLowerCase().trim();
          const d = (item.denomination || 'unknown').toLowerCase().trim();
          let key;
          
          if (includeYear) {
               const y = (item.year || 'unknown').toString().toLowerCase().trim();
               key = `${c}|${y}|${d}`;
          } else {
               key = `${c}|${d}`;
          }
          
          if (!variantMap.has(key)) {
              variantMap.set(key, []);
          }
          variantMap.get(key).push(item);
      });

      const processed = [];
      variantMap.forEach((group) => {
          // Always sort variants by ID or Creation date to keep order stable
          group.sort((a, b) => b.id - a.id);
          
          if (group.length === 1) {
              processed.push({ ...group[0], _isVariantStack: false });
          } else {
              // It's a stack of variants
              // Use the newest one (first in sorted) as the representative
              const main = group[0];
              const c = (main.country || 'unknown').toLowerCase().trim();
              const d = (main.denomination || 'unknown').toLowerCase().trim();
              const y = (main.year || 'unknown').toString().toLowerCase().trim();
              
              const groupKey = includeYear ? `${c}|${y}|${d}` : `${c}|${d}`;

              processed.push({
                  ...main,
                  _isVariantStack: true,
                  _variantGroupKey: groupKey, // ID for expansion state
                  _variants: group,
                  _count: group.length
              });
          }
      });
      
      // Sort the whole list (optional, e.g. by latest)
      processed.sort((a, b) => b.id - a.id);
      
      return processed;
  }, [items, settings]);

  // 2. Apply "Section Grouping" if selected (Country, Type, etc.)
  const groupedSections = useMemo(() => {
    if (groupBy === 'none') return null;
    
    const groups = {};
    displayItems.forEach(item => {
      let key = item[groupBy];
      // Handle null/undefined values
      if (key === null || key === undefined || key === '') {
        key = 'Unknown';
      }
      
      // Special handling
      if (groupBy === 'rarity') {
        key = key.replace(/_/g, ' ');
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    
    // Sort keys alphabetically
    return Object.keys(groups).sort().reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, [displayItems, groupBy]);

  const toggleVariant = (key) => {
      setExpandedVariants(prev => ({
          ...prev,
          [key]: !prev[key]
      }));
  };

  const toggleFlip = (itemId) => {
      setFlippedCards(prev => ({
          ...prev,
          [itemId]: !prev[itemId]
      }));
  };

  // If parent controls groupBy via prop, we prefer that. Otherwise keep local state.
  const currentGroupBy = groupByProp ?? groupBy;

  if (!items || items.length === 0) {
    return (
      <div className={`text-center py-20 ${themeClasses.textMuted}`}>
        <p className="text-lg">No items found in the database.</p>
      </div>
    );
  }

  const renderCard = (item) => {
      const isStack = item._isVariantStack;
      const isExpanded = isStack && expandedVariants[item._variantGroupKey];
      
      // Determine which side to show (prioritize back, show front if flipped or no back)
      const hasBack = item.image_back_path;
      const hasFront = item.image_front_path;
      const hasBoth = hasBack && hasFront;
      const isFlipped = flippedCards[item.id];
      
      // Default: show back if available, otherwise front, otherwise main image_path
      let currentImagePath;
      let currentLabel;
      if (hasBoth) {
          // If both sides exist, show back by default, front when flipped
          currentImagePath = isFlipped ? item.image_front_path : item.image_back_path;
          currentLabel = isFlipped ? 'Fronte' : 'Retro';
      } else if (hasBack) {
          currentImagePath = item.image_back_path;
          currentLabel = 'Retro';
      } else if (hasFront) {
          currentImagePath = item.image_front_path;
          currentLabel = 'Fronte';
      } else {
          currentImagePath = item.image_path;
          currentLabel = null;
      }

        const thumbVersion = encodeURIComponent(item.updated_at || item.created_at || item.id);

      return (
        <div key={item.id} className={`group ${themeClasses.card} border hover:border-current transition-all p-3`}>
            <div className="relative aspect-[4/3] bg-black mb-3 overflow-hidden rounded-sm grayscale group-hover:grayscale-0 transition-all duration-700">
            {currentImagePath ? (
              <HoverImage 
                key={currentImagePath}
                src={`/api/uploads/${currentImagePath}`} 
                thumbSrc={`/api/uploads/thumbnails/${currentImagePath}?v=${thumbVersion}`}
                alt={`${item.type} from ${item.country} - ${currentLabel || ''}`}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                No Image
              </div>
            )}
            
            {/* Flip button for front/back - centered on right side */}
            {hasBoth && (
                <button
                  className={`absolute top-1/2 -translate-y-1/2 right-2 p-1.5 ${themeClasses.bg} border border-inherit rounded-sm hover:${themeClasses.accent} transition-colors opacity-80 hover:opacity-100`}
                  onClick={(e) => { e.stopPropagation(); toggleFlip(item.id); }}
                  title={`Mostra ${isFlipped ? 'Retro' : 'Fronte'}`}
                >
                  <ArrowLeftRight size={12} />
                </button>
            )}
            
            {/* Label for current side */}
            {currentLabel && (
                <div className={`absolute bottom-2 right-2 px-2 py-0.5 ${themeClasses.bg} border border-inherit text-[7px] tracking-widest ${themeClasses.textMuted} rounded-sm opacity-80`}>
                    {currentLabel}
                </div>
            )}
            
            {/* Badge for variants */}
            {isStack && (
                <div 
                  className={`absolute top-2 right-2 px-2 py-1 ${themeClasses.accentBg} text-white text-[8px] tracking-widest rounded-sm cursor-pointer`}
                  onClick={(e) => { e.stopPropagation(); toggleVariant(item._variantGroupKey); }}
                >
                    {item._count} Copies
                </div>
            )}
            
            {/* Numista Badge */}
            {item.numista_collection_id && (
                <div className={`absolute top-2 left-2 px-2 py-1 ${isDarkMode ? 'bg-blue-600' : 'bg-blue-500'} text-white text-[8px] rounded-sm`} title="In Numista Collection">
                    N
                </div>
            )}

            {item.year && (
              <div className={`absolute bottom-0 left-0 px-2 py-0.5 ${themeClasses.bg} text-[8px] tracking-widest ${themeClasses.accent} border-r border-t border-inherit`}>
                {item.year}
              </div>
            )}
          </div>
          
          <div className="space-y-1">
            <div className="flex justify-between items-start">
              <h4 className="text-xs font-bold tracking-tight group-hover:text-current transition-colors uppercase">{item.country}</h4>
              <span className={`text-[9px] ${themeClasses.textMuted} font-mono`}>#{item.id}</span>
            </div>
            
            <div className="flex gap-1 flex-wrap">
              {item.denomination && <span className={`text-[9px] ${themeClasses.textMuted} tracking-widest uppercase`}>{item.denomination}</span>}
              {item.currency && <span className={`text-[9px] ${themeClasses.textMuted}`}>• {item.currency}</span>}
            </div>
    
            {item.rarity && (
              <div className={`text-[8px] tracking-wider uppercase ${themeClasses.accent} mt-1`}>
                {item.rarity.replace('_', ' ')}
              </div>
            )}
    
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-inherit transition-colors">
              {item.estimated_value && (
                <span className="text-[10px] font-mono font-bold tracking-tighter">€{item.estimated_value}</span>
              )}
              <div className="flex gap-1">
                {isStack ? (
                  <button 
                    onClick={() => toggleVariant(item._variantGroupKey)} 
                    className={`text-[8px] px-2 py-1 ${themeClasses.accentBg} text-white rounded-sm tracking-wide uppercase`}
                  >
                    {isExpanded ? 'Hide' : `${item._count}`}
                  </button>
                ) : (
                  <>
                    <button 
                      onClick={() => onEdit(item)} 
                      className={`p-1 hover:${themeClasses.accent} ${themeClasses.textMuted} transition-colors`}
                    >
                      <Info size={12} />
                    </button>
                    <button 
                      onClick={() => onDelete(item.id)} 
                      className={`p-1 hover:text-red-500 ${themeClasses.textMuted} transition-colors text-xs`}
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Variants List inside the Card */}
          {isExpanded && (
              <div className={`mt-3 pt-3 border-t border-inherit space-y-2`}>
                  <h4 className={`text-[9px] tracking-widest uppercase ${themeClasses.accent} mb-2`}>Variants</h4>
                  {item._variants.map(variant => {
                      const vHasBack = variant.image_back_path;
                      const vHasFront = variant.image_front_path;
                      const vIsFlipped = flippedCards[variant.id];
                      
                      let vImagePath;
                      if (vHasBack && vHasFront) {
                          vImagePath = vIsFlipped ? variant.image_front_path : variant.image_back_path;
                      } else if (vHasBack) {
                          vImagePath = variant.image_back_path;
                      } else if (vHasFront) {
                          vImagePath = variant.image_front_path;
                      } else {
                          vImagePath = variant.image_path;
                      }

                        const variantThumbVersion = encodeURIComponent(variant.updated_at || variant.created_at || variant.id);
                      
                      return (
                      <div key={variant.id} className={`flex items-center gap-2 p-2 ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'} rounded-sm`}>
                           <div className="w-12 h-12 flex-shrink-0 rounded-sm overflow-hidden bg-black relative">
                               {vImagePath && (
                                   <HoverImage 
                                     key={vImagePath}
                                     src={`/api/uploads/${vImagePath}`} 
                                     thumbSrc={`/api/uploads/thumbnails/${vImagePath}?v=${variantThumbVersion}`}
                                     alt="variant" 
                                   />
                               )}
                               {vHasBack && vHasFront && (
                                   <button
                                     className="absolute bottom-0 left-0 p-0.5 bg-black/50 hover:bg-black/70"
                                     onClick={(e) => { e.stopPropagation(); toggleFlip(variant.id); }}
                                   >
                                     <ArrowLeftRight size={8} />
                                   </button>
                               )}
                           </div>
                           <div className="flex-1 text-[9px]">
                               <div>ID: {variant.id}</div>
                               <div className={themeClasses.textMuted}>{variant.condition || 'N/A'}</div>
                               {variant.numista_collection_id && (
                                   <span className="text-blue-500 text-[8px]">Numista</span>
                               )}
                           </div>
                           <div className="flex gap-1">
                               <button 
                                 onClick={() => onEdit(variant)} 
                                 className={`p-1 hover:${themeClasses.accent} ${themeClasses.textMuted}`}
                               >
                                 ✎
                               </button>
                               <button 
                                 onClick={() => onDelete(variant.id)} 
                                 className={`p-1 hover:text-red-500 ${themeClasses.textMuted}`}
                               >
                                 ×
                               </button>
                           </div>
                      </div>
                  )})}
              </div>
          )}
        </div>
      );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-sm ${themeClasses.textMuted}`}>Collection ({items.length} items)</h2>
        <div className="flex items-center gap-2">
          <label className={`text-[9px] tracking-widest uppercase ${themeClasses.textMuted}`}>Group by: </label>
          <select 
            value={currentGroupBy} 
            onChange={(e) => {
              const val = e.target.value;
              if (typeof onGroupByChange === 'function') onGroupByChange(val);
              else setGroupBy(val);
            }}
            className={`text-[9px] px-2 py-1 border ${themeClasses.input} ${themeClasses.textMain} rounded-sm tracking-widest uppercase`}
          >
            <option value="none">None</option>
            <option value="country">Country</option>
            <option value="year">Year</option>
            <option value="type">Type</option>
            <option value="rarity">Rarity</option>
          </select>
        </div>
      </div>

      {groupedSections ? (
        <div className="space-y-8">
          {Object.entries(groupedSections).map(([key, groupItems]) => (
            <div key={key}>
              <h3 className={`text-[10px] tracking-[0.3em] font-bold uppercase mb-4 flex items-center ${themeClasses.accent}`}>
                <div className={`w-1 h-3 ${themeClasses.accentBg} mr-3`} />
                {key} ({groupItems.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {groupItems.map(renderCard)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {displayItems.map(renderCard)}
        </div>
      )}
    </div>
  );
};

export default ItemList;
